import { useVirtualizer } from '@tanstack/react-virtual';
import { type ReactNode, useEffect, useRef } from 'react';

export const VirtualizedList = <D extends object>({
    list,
    indexToScrollTo,
    children,
    gap,
    ...rest
}: {
    list: Array<D>;
    indexToScrollTo?: number | null;
    children: (item: NoInfer<D>, index: number) => ReactNode;
    gap?: number;
} & ({ fixHeight: number } | { estimateHeight: (node: D) => number })) => {
    const parentRef = useRef<HTMLDivElement>(null);

    const virtualizer = useVirtualizer({
        count: list.length,
        getScrollElement: () => parentRef.current,
        estimateSize: (index) => {
            if ('fixHeight' in rest) {
                return rest.fixHeight;
            } else {
                // biome-ignore lint/style/noNonNullAssertion: does not apply
                return rest.estimateHeight(list.at(index)!);
            }
        },
        gap,
    });

    useEffect(() => {
        if (indexToScrollTo && indexToScrollTo > -1) {
            const isElementVisible = Boolean(
                virtualizer
                    .getVirtualItems()
                    .find(({ index }) => index === indexToScrollTo)
            );
            if (!isElementVisible) {
                virtualizer.scrollToIndex(indexToScrollTo, {
                    align: 'center',
                    behavior: 'smooth',
                });
            }
        }
    }, [indexToScrollTo]);

    return (
        <div
            ref={parentRef}
            style={{ height: '100%', overflowY: 'auto' }}
        >
            <div
                style={{
                    position: 'relative',
                    height: `${virtualizer.getTotalSize()}px`,
                }}
            >
                {virtualizer.getVirtualItems().map((virtualRow) => (
                    <div
                        key={virtualRow.key}
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: `${virtualRow.size}px`,
                            transform: `translateY(${virtualRow.start}px)`,
                        }}
                    >
                        {children(
                            // biome-ignore lint/style/noNonNullAssertion: does not apply
                            list.at(virtualRow.index)!,
                            virtualRow.index
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};
